import {Component, computed, inject, input, output, signal, effect} from '@angular/core';
import {ProjectStepRichTextService} from '../../../../../../../shared/services/project-step-rich-text.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {
  InvitationOfferOverviewResponse,
  ProviderTenderServiceFeesUpsertRequest,
  ProviderTenderImplementationFeesUpsertRequest,
  YearlyFeeUpsertRequest,
  ProviderTenderIntegrationSetupFeesUpsertRequest,
  ProviderTenderIntegrationOtherFeesUpsertRequest,
} from '@frontend/shared-api';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {CompanyEmployeeInfoComponent} from './company-employee-info/company-employee-info.component';
import {CompanyEmployeeContainer} from './company-employee-info/company-employee.types';
import {
  FunctionalityFeeModuleData,
  ServiceFeesContainerData,
} from './service-fees/service-fees-container.types';
import {switchMap} from 'rxjs';
import {ModuleFunction, DirectusService} from '@frontend/directus';
import {map} from 'rxjs/operators';
import {ImplementationFeesContainer} from './implementation-fees/implementation-fees.types';
import {IntegrationSetupFeesContainer} from './integration-setup-fees/integration-setup-fees.types';
import {IntegrationOtherFeesContainer} from './integration-other-fees/integration-other-fees.types';
import {SumFees} from './step-8-2-3-rfq-preview-price-list.types';
import {TotalFeesModuleComponent} from './total-fees/total-fees.component';
import {TotalFeeData, TotalFeesContainer} from './total-fees/total-fees.types';
import {IntegrationOtherFeesComponent} from './integration-other-fees/integration-other-fees.component';
import {IntegrationSetupFeesComponent} from './integration-setup-fees/integration-setup-fees.component';
import {ImplementationFeesComponent} from './implementation-fees/implementation-fees.component';
import {ServiceFeesComponent} from './service-fees/service-fees.component';
import {TotalFeeCardComponent} from './components/total-fee-card/total-fee-card.component';
import {TranslocoPipe} from '@jsverse/transloco';
import {RfqFeeCategoryService} from '../../../../../../../shared/services/rfq-fee-category.service';

@Component({
  selector: 'app-step-8-2-3-rfq-preview-price-list',
  providers: [ProjectStepRichTextService],
  imports: [
    CompanyEmployeeInfoComponent,
    TotalFeesModuleComponent,
    IntegrationOtherFeesComponent,
    IntegrationSetupFeesComponent,
    ImplementationFeesComponent,
    ServiceFeesComponent,
    TotalFeeCardComponent,
    TranslocoPipe,
  ],
  templateUrl: './step-8-2-3-rfq-preview-price-list.component.html',
})
export class Step823RfqPreviewPriceListComponent {
  private readonly feeCategories = inject(RfqFeeCategoryService);
  private readonly directusService = inject(DirectusService);

  readonly invitationOfferOverview = input.required<InvitationOfferOverviewResponse>();
  readonly isCustomerType = input<boolean>(true);
  readonly stepLocked = input<boolean>(false);
  readonly isReadOnly = input<boolean>(false);
  readonly isDraft = input<boolean>(false);
  readonly statusChange = output<TabStatus>();

  readonly directusFunctionIds = computed(
    () =>
      this.invitationOfferOverview().serviceFeesResponse?.map((f) => f.moduleFunctionDirectusId) ??
      [],
  );

  readonly moduleFunctionMap = toSignal(
    toObservable(this.directusFunctionIds).pipe(
      switchMap((ids) => this.directusService.loadModuleFunctionBy(ids)),
      map((functions) => new Map(functions.map((f) => [f.id, f]))),
    ),
    {initialValue: new Map<number, ModuleFunction>()},
  );

  readonly companyEmployeesContainer = computed(() => {
    const current = this.invitationOfferOverview().companyInfosResponse;

    return (
      current?.map((data): CompanyEmployeeContainer => {
        const salariedHeadcount = data.salariedHeadcount ?? 0;
        const industrialHeadcount = data.industrialHeadcount ?? 0;
        const contingentHeadcount = data.contingentHeadcount ?? 0;

        return {
          office: data.officeLookupResponse,
          salariedHeadcount,
          industrialHeadcount,
          contingentHeadcount,
          totalHeadcount: salariedHeadcount + industrialHeadcount + contingentHeadcount,
          totalFte: (data.salariedFte ?? 0) + (data.industrialFte ?? 0) + (data.contingentFte ?? 0),
        };
      }) ?? []
    );
  });

  readonly scopeYears = computed(() => {
    const length = this.invitationOfferOverview().yearsInScope;
    return Array.from({length}, (_, i) => i + 1);
  });

  readonly serviceFeesContainer = computed(() => {
    const current = this.invitationOfferOverview();
    const companyEmployeesData = this.companyEmployeesContainer();
    const functionMap = this.moduleFunctionMap();

    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      companyEmployeesData: companyEmployeesData,
      scopeYears: this.scopeYears(),
      data: current.serviceFeesResponse.map(
        (data) =>
          ({
            moduleFunction: functionMap.get(data.moduleFunctionDirectusId),
            offices: data.officeProjectsResponse,
            eligibleHeadcount: data.eligibleHeadcount,
            feePerUnit: data.feePerUnit,
            comment: data.comment,
          }) as FunctionalityFeeModuleData,
      ),
    } as ServiceFeesContainerData;
  });

  protected serviceSumFees = signal<SumFees>({} as SumFees);
  readonly submitServiceFees = output<ProviderTenderServiceFeesUpsertRequest[]>();

  protected saveServiceFees($event: ServiceFeesContainerData) {
    const resList = $event.data.map((fee) => {
      return {
        moduleFunctionDirectusId: fee.moduleFunction.id,
        eligibleHeadcount: fee.eligibleHeadcount,
        feePerUnit: fee.feePerUnit,
        comment: fee.comment,
      } as ProviderTenderServiceFeesUpsertRequest;
    });
    this.submitServiceFees.emit(resList);
  }

  readonly implementationFeesContainer = computed(() => {
    const current = this.invitationOfferOverview();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      scopeYears: this.scopeYears(),
      data: current.implementationActivityFeesResponse,
    } as ImplementationFeesContainer;
  });

  protected implementationActivitySumFees = signal<SumFees>({} as SumFees);
  readonly submitImplementationFees = output<ProviderTenderImplementationFeesUpsertRequest[]>();

  protected saveImplementationFees($event: ImplementationFeesContainer) {
    const resList = $event.data.map((fee) => {
      return {
        implementationActivityDirectusUuid: fee.implementationActivityDirectusUuid,
        hourlyRate: fee.hourlyRate,
        comment: fee.comment,
        implementationYearlyFees: (fee.implementationFunctionFeesResponse ?? []).map(
          (func) =>
            ({
              moduleFunctionDirectusId: func.moduleFunctionDirectusId,
              yearlyFees: (func.implementationYearlyFees ?? []).map((y: any) => y.feePerUnit ?? 0),
            }) as YearlyFeeUpsertRequest,
        ),
      } as ProviderTenderImplementationFeesUpsertRequest;
    });

    this.submitImplementationFees.emit(resList);
  }

  readonly integrationSetupFeesContainer = computed(() => {
    const current = this.invitationOfferOverview();

    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      scopeYears: this.scopeYears(),
      data: current.integrationSetupFeesResponse,
    } as IntegrationSetupFeesContainer;
  });

  protected integrationSetupSumFees = signal<SumFees>({} as SumFees);

  readonly submitIntegrationSetupFees = output<ProviderTenderIntegrationSetupFeesUpsertRequest[]>();

  protected saveIntegrationSetupFees($event: IntegrationSetupFeesContainer) {
    const resList = $event.data.map((fee) => {
      return {
        uniqueIdentifier: fee.uniqueIdentifier,
        directusComplexityUuid: fee.directusComplexityUuid,
        yearlyFees: (fee.yearlyItemFees ?? []).map((y: any) => y.feePerUnit ?? 0),
        comment: fee.comment,
      } as ProviderTenderIntegrationSetupFeesUpsertRequest;
    });

    this.submitIntegrationSetupFees.emit(resList);
  }

  readonly integrationOtherFeesContainer = computed(() => {
    const current = this.invitationOfferOverview();

    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      scopeYears: this.scopeYears(),
      data: current.integrationOtherFeesResponse,
    } as IntegrationOtherFeesContainer;
  });

  readonly submitIntegrationOtherFees = output<ProviderTenderIntegrationOtherFeesUpsertRequest[]>();

  protected saveIntegrationOtherFees($event: IntegrationOtherFeesContainer) {
    const resList = $event.data.map((fee) => {
      return {
        directusIntegrationOtherFeeUuid: fee.directusDropdownOtherFeeUuid,
        yearlyFees: (fee.yearlyItemFees ?? []).map((y: any) => y.feePerUnit ?? 0),
        comment: fee.comment,
      } as ProviderTenderIntegrationOtherFeesUpsertRequest;
    });

    this.submitIntegrationOtherFees.emit(resList);
  }

  protected integrationOtherSumFees = signal<SumFees>({} as SumFees);

  readonly totalFeesContainer = computed(() => {
    const current = this.invitationOfferOverview();

    const serviceSumFeesCard = this.serviceSumFeesCard();
    const implementationActivitySumFeesCard = this.implementationActivitySumFeesCard();
    const integrationSetupSumFeesCard = this.integrationSetupSumFeesCard();
    const integrationOtherSumFeesCard = this.integrationOtherSumFeesCard();

    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      scopeYears: this.scopeYears(),
      data: [
        serviceSumFeesCard,
        implementationActivitySumFeesCard,
        integrationSetupSumFeesCard,
        integrationOtherSumFeesCard,
      ],
    } as TotalFeesContainer;
  });

  protected totalModuleSumFees = signal<SumFees>({} as SumFees);

  protected readonly serviceSumFeesCard = computed(() => {
    const current = this.invitationOfferOverview();
    const serviceSumFees = this.serviceSumFees();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      icon: 'pi pi-dollar',
      color: 'text-orange-500',
      categoryTitle: 'project.steps.rfq.invitationPreview.previewPriceList.cardTitle.service.fees',
      categoryLabel: this.feeCategories.findLabel('service').title,
      sumFees: serviceSumFees,
    } as TotalFeeData;
  });

  protected readonly implementationActivitySumFeesCard = computed(() => {
    const current = this.invitationOfferOverview();
    const implementationActivitySumFees = this.implementationActivitySumFees();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      icon: 'pi pi-cog',
      color: 'text-fuchsia-500',
      categoryTitle:
        'project.steps.rfq.invitationPreview.previewPriceList.cardTitle.implementation.fees',
      categoryLabel: this.feeCategories.findLabel('implementation').title,
      sumFees: implementationActivitySumFees,
    } as TotalFeeData;
  });
  protected readonly integrationSetupSumFeesCard = computed(() => {
    const current = this.invitationOfferOverview();
    const integrationSetupSumFees = this.integrationSetupSumFees();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      icon: 'pi pi-link',
      color: 'text-blue-500',
      categoryTitle:
        'project.steps.rfq.invitationPreview.previewPriceList.cardTitle.integrationSetup.fees',
      categoryLabel: this.feeCategories.findLabel('integrationSetup').title,
      sumFees: integrationSetupSumFees,
    } as TotalFeeData;
  });
  protected readonly integrationOtherSumFeesCard = computed(() => {
    const current = this.invitationOfferOverview();
    const integrationOtherSumFees = this.integrationOtherSumFees();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      icon: 'pi pi-folder-open',
      color: 'text-gray-300',
      categoryTitle:
        'project.steps.rfq.invitationPreview.previewPriceList.cardTitle.otherImplementation.fees',
      categoryLabel: this.feeCategories.findLabel('other').title,
      sumFees: integrationOtherSumFees,
    } as TotalFeeData;
  });

  protected readonly totalSumFeesCard = computed(() => {
    const current = this.invitationOfferOverview();
    const totalModuleSumFees = this.totalModuleSumFees();
    return {
      locale: current.locale,
      currencyCode: current.currencyCode,
      icon: 'pi pi-globe',
      color: 'text-green-500',
      categoryTitle: 'project.steps.rfq.invitationPreview.previewPriceList.cardTitle.total.fees',
      sumFees: totalModuleSumFees,
    } as TotalFeeData;
  });

  readonly totalFeesCard = output<TotalFeeData>();

  constructor() {
    effect(() => {
      this.totalFeesCard.emit(this.totalSumFeesCard());
    });
  }
}
